/* The Italian Chocolate 1971 · tic-collection.js (collection pages). No dependencies.
   Progressive enhancement only: without this file every option is a working link and all text shows.
   1. Segmented control. On pages with both lines, the options of a dimension with 2+ values
      ([data-tic-filter="format"|"line"]) become a radio group (role="radio", aria-checked, roving
      tabindex, arrow keys / Home / End) that filters the cards in place. The result is announced in a
      polite live region; the choice is kept in the URL (?box=…&line=…, replaceState), so Back from a
      product page returns to the same view. A black "ink" layer with a white copy of the labels is
      clipped to the chosen option (clip-path, 260 ms ease-out). Navigation rows ([data-tic-nav]) stay
      links and get the same ink on the current page; the format links carry the chosen line
      (?line=…) to the next page. (Not "?format=": Shopify reserves that parameter and answers 404.)
   2. Cards and line bands that stay in place-size move to their new place (FLIP: translate only,
      280 ms). Those that appear, change size or would travel more than 160 px fade in and rise 8 px,
      30 ms apart: nothing is stretched and nothing flies in from off-screen.
      Reduced motion: no movement; only the blocks that appear fade in (160 ms).
   3. Layout state: data-tic-row on the grid = cards in the fullest row (CSS lays out rows of 1 and 2
      differently from 750 px); on phones the first visible box of each line spans the width when the
      count is odd (.is-lead). Every card image gets the matching sizes attribute.
   4. Header description on phones: the paragraphs after the first wait behind "Read more"
      (aria-expanded); on wider screens everything shows.
   A paginated grid ([data-tic-paged], > 24 products) is left as links: it cannot filter what is not
   on the page. */
(() => {
  if (window.TICC) return;
  window.TICC = { v: 2 };
  const doc = document;
  const EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const phone = window.matchMedia('(max-width: 749px)');
  const design = !!(window.Shopify && window.Shopify.designMode);
  const PARAM = { format: 'box', line: 'line' }; // URL keys ("format" is reserved by Shopify: 404)
  const ON = ':scope > .tic-seg__opt[aria-checked="true"], :scope > .tic-seg__opt[aria-current="page"]';

  /* ---------- ink layer ---------- */
  const buildInk = (track) => {
    let ink = track.querySelector(':scope > .tic-seg__ink');
    if (ink) ink.remove();
    ink = doc.createElement('div');
    ink.className = 'tic-seg__ink is-static';
    ink.setAttribute('aria-hidden', 'true');
    track.querySelectorAll(':scope > .tic-seg__opt').forEach((opt) => {
      const s = doc.createElement('span');
      s.className = 'tic-seg__opt';
      s.textContent = opt.textContent;
      ink.appendChild(s);
    });
    track.appendChild(ink);
    track.classList.add('has-ink');
    return ink;
  };
  const placeInk = (track, animate) => {
    const ink = track.querySelector(':scope > .tic-seg__ink');
    if (!ink) return;
    const active = track.querySelector(ON);
    ink.classList.toggle('is-static', !animate || reduce.matches);
    if (!active) {
      ink.style.clipPath = 'inset(0 100% 0 0 round 6px)';
      return;
    }
    const left = active.offsetLeft - ink.offsetLeft;
    const right = ink.offsetWidth - left - active.offsetWidth;
    ink.style.clipPath = `inset(0 ${Math.max(0, right)}px 0 ${Math.max(0, left)}px round 6px)`;
    if (!animate) void ink.offsetWidth; // commit the static position before transitions return
  };
  // The row scrolls only as a last resort (very long translations): fade its edge while there is more.
  const fadeEdge = (track) => {
    const sc = track.closest('.tic-seg__scroll');
    if (!sc) return;
    const over = sc.scrollWidth > sc.clientWidth + 1;
    const atEnd = sc.scrollLeft + sc.clientWidth >= sc.scrollWidth - 2;
    sc.classList.toggle('is-overflow', over && !atEnd);
  };
  // Keep the chosen option in view on narrow screens (no smooth scroll on load).
  const revealOpt = (track, smooth) => {
    const sc = track.closest('.tic-seg__scroll');
    const active = track.querySelector(ON);
    if (!sc || !active || sc.scrollWidth <= sc.clientWidth) return;
    const l = active.offsetLeft - 16;
    const r = active.offsetLeft + active.offsetWidth + 16 - sc.clientWidth;
    const to = l < sc.scrollLeft ? l : r > sc.scrollLeft ? r : sc.scrollLeft;
    sc.scrollTo({ left: to, behavior: smooth && !reduce.matches ? 'smooth' : 'auto' });
  };
  const watchTracks = (tracks) => {
    const all = () => tracks.forEach((t) => { placeInk(t, false); fadeEdge(t); });
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(all);
      tracks.forEach((t) => { ro.observe(t); const sc = t.closest('.tic-seg__scroll'); if (sc) ro.observe(sc); });
    }
    tracks.forEach((t) => {
      const sc = t.closest('.tic-seg__scroll');
      if (sc) sc.addEventListener('scroll', () => fadeEdge(t), { passive: true });
    });
    if ('MutationObserver' in window) {
      tracks.forEach((t) => {
        new MutationObserver(() => {
          // labels translated in place (GTranslate): copy them to the ink so both layers match
          const ink = t.querySelector(':scope > .tic-seg__ink');
          const opts = t.querySelectorAll(':scope > .tic-seg__opt');
          if (!ink) return;
          ink.querySelectorAll('.tic-seg__opt').forEach((s, i) => { if (opts[i] && s.textContent !== opts[i].textContent) s.textContent = opts[i].textContent; });
          placeInk(t, false);
          fadeEdge(t);
        }).observe(t, { subtree: true, characterData: true, childList: true });
      });
    }
    phone.addEventListener('change', all);
    all();
  };

  /* ---------- grid ---------- */
  const setupGrid = (sec) => {
    if (sec.dataset.ticReady) return;
    sec.dataset.ticReady = '1';
    const grid = sec.querySelector('[data-tic-grid]');
    const segs = [...sec.querySelectorAll('.tic-seg')];
    const tracks = segs.map((s) => s.querySelector('.tic-seg__track')).filter(Boolean);
    const paged = sec.hasAttribute('data-tic-paged');

    if (!grid || paged) {
      tracks.forEach((t) => { buildInk(t); placeInk(t, false); revealOpt(t, false); });
      watchTracks(tracks);
      return;
    }

    const cells = [...grid.querySelectorAll('[data-tic-cell]')];
    const groups = [...grid.querySelectorAll('[data-tic-group]')];
    const bands = [...grid.querySelectorAll('.tic-cg__band')];
    const movers = [...bands, ...cells];
    const status = sec.querySelector('[data-tic-status]');
    const empty = sec.querySelector('[data-tic-empty]');
    const flatBase = grid.hasAttribute('data-tic-flat');
    const mixed = grid.classList.contains('is-mixed');
    const sizes = { half: grid.dataset.sizesHalf, lead: grid.dataset.sizesLead, wide: grid.dataset.sizesWide, one: grid.dataset.sizesOne };
    const state = { format: 'all', line: 'all' };
    const labels = { format: {}, line: {} };
    const filterSegs = segs.filter((s) => s.dataset.ticFilter === 'format' || s.dataset.ticFilter === 'line');
    const radios = (seg) => [...seg.querySelectorAll('.tic-seg__track > button.tic-seg__opt')];

    // Links -> one radio group per dimension (same classes and label). Only dimensions with 2+ values here.
    filterSegs.forEach((seg) => {
      const key = seg.dataset.ticFilter;
      const track = seg.querySelector('.tic-seg__track');
      seg.setAttribute('role', 'radiogroup');
      track.querySelectorAll(':scope > a.tic-seg__opt').forEach((a) => {
        const b = doc.createElement('button');
        b.type = 'button';
        b.className = a.className;
        b.dataset.ticVal = a.dataset.ticVal || 'all';
        b.textContent = a.textContent;
        b.setAttribute('role', 'radio');
        const on = b.dataset.ticVal === 'all';
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
        labels[key][b.dataset.ticVal] = b.textContent.trim();
        a.replaceWith(b);
      });
      track.addEventListener('click', (e) => {
        const b = e.target.closest('button.tic-seg__opt');
        if (!b || !track.contains(b)) return;
        apply({ [key]: b.dataset.ticVal }, true);
      });
      // radio keys: arrows move the choice (and the focus), Home / End jump to the ends
      track.addEventListener('keydown', (e) => {
        const list = radios(seg);
        const i = list.indexOf(e.target);
        if (i < 0) return;
        const n = list.length;
        let j = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') j = (i + 1) % n;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') j = (i - 1 + n) % n;
        else if (e.key === 'Home') j = 0;
        else if (e.key === 'End') j = n - 1;
        if (j < 0) return;
        e.preventDefault();
        list[j].focus();
        apply({ [key]: list[j].dataset.ticVal }, true);
      });
    });
    tracks.forEach((t) => buildInk(t));

    const valid = (key, v) => !!v && filterSegs.some((s) => s.dataset.ticFilter === key && s.querySelector(`[data-tic-val="${CSS.escape(v)}"]`));
    const match = (c) =>
      (state.format === 'all' || c.dataset.ticFormat === state.format) &&
      (state.line === 'all' || c.dataset.ticLine === state.line);

    const say = (n) => {
      if (!status) return;
      const one = sec.dataset.statusOne || '1 box shown';
      const many = sec.dataset.statusMany || '[count] boxes shown';
      const parts = [];
      if (state.format !== 'all' && labels.format[state.format]) parts.push(labels.format[state.format]);
      if (state.line !== 'all' && labels.line[state.line]) parts.push(labels.line[state.line]);
      const head = (n === 1 ? one : many).replace('[count]', n);
      // clear first so the same sentence is announced again
      status.textContent = '';
      window.setTimeout(() => { status.textContent = parts.length ? `${head}: ${parts.join(', ')}.` : `${head}.`; }, 60);
    };

    // format links (Pralines / Filled Bars pages) keep the chosen line on the next page
    const syncNav = () => {
      if (!filterSegs.some((s) => s.dataset.ticFilter === 'line')) return;
      sec.querySelectorAll('[data-tic-nav="format"] a.tic-seg__opt').forEach((a) => {
        try {
          const u = new URL(a.getAttribute('href'), window.location.href);
          if (state.line === 'all') u.searchParams.delete(PARAM.line);
          else u.searchParams.set(PARAM.line, state.line);
          a.setAttribute('href', u.pathname + u.search);
        } catch (e) { /* ignore */ }
      });
    };

    const layout = () => {
      const shown = cells.filter((c) => !c.hidden);
      const visGroups = groups.filter((g) => !g.hidden);
      const per = (g) => [...g.querySelectorAll('[data-tic-cell]')].filter((c) => !c.hidden);
      const flat = flatBase || (visGroups.length > 1 && visGroups.every((g) => per(g).length <= 1));
      grid.classList.toggle('is-flat', flat);
      const row = flat ? shown.length : Math.max(0, ...visGroups.map((g) => per(g).length));
      grid.dataset.ticRow = String(row);
      cells.forEach((c) => c.classList.remove('is-lead'));
      const lead = (list) => { if (list.length % 2 === 1) list[0].classList.add('is-lead'); };
      if (flat) lead(shown);
      else visGroups.forEach((g) => { const l = per(g); if (l.length) lead(l); });
      const wide = row === 2 && (flat || !mixed);
      cells.forEach((c) => {
        const s = row === 1 ? sizes.one : wide ? sizes.wide : c.classList.contains('is-lead') ? sizes.lead : sizes.half;
        if (s) c.querySelectorAll('.tic-card__img').forEach((i) => { if (i.getAttribute('sizes') !== s) i.setAttribute('sizes', s); });
      });
      return shown;
    };

    let first = true;
    const apply = (patch, animate) => {
      const next = { ...state, ...patch };
      if (next.format === state.format && next.line === state.line && !first) return;
      const moving = animate && !reduce.matches;
      // what is on screen now (and where, when it may move)
      const before = new Map();
      const shownNow = (el) => el.getClientRects().length > 0; // false under display: none / hidden
      if (animate) movers.forEach((el) => { if (shownNow(el)) before.set(el, moving ? el.getBoundingClientRect() : null); });
      movers.forEach((el) => el.getAnimations().forEach((a) => a.cancel()));
      if (first) {
        // the filter takes over from the scroll reveal: no second, slower entrance on these blocks
        movers.forEach((c) => { c.style.transition = 'none'; c.classList.add('is-in'); });
        first = false;
      }
      Object.assign(state, next);

      cells.forEach((c) => { c.hidden = !match(c); });
      groups.forEach((g) => { g.hidden = !g.querySelector('[data-tic-cell]:not([hidden])'); });
      const shown = layout();
      if (empty) empty.hidden = shown.length > 0;

      // controls: one checked radio per group, and only that one in the tab order
      filterSegs.forEach((seg) => {
        const key = seg.dataset.ticFilter;
        radios(seg).forEach((b) => {
          const on = b.dataset.ticVal === state[key];
          b.setAttribute('aria-checked', on ? 'true' : 'false');
          b.tabIndex = on ? 0 : -1;
        });
      });
      tracks.forEach((t) => { placeInk(t, animate); if (animate) revealOpt(t, true); fadeEdge(t); });
      syncNav();

      // motion
      if (animate) {
        let k = 0;
        const rise = (c) => {
          c.animate(
            [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }],
            { duration: 240, easing: EASE, delay: Math.min(k, 6) * 30, fill: 'backwards' }
          );
          k += 1;
        };
        movers.filter(shownNow).forEach((c) => {
          const had = before.has(c);
          if (!moving) {
            // reduced motion: no movement; only what was not on screen fades in
            if (!had) c.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160, easing: 'ease' });
            return;
          }
          const b = before.get(c);
          if (!had || !b) { rise(c); return; }
          const a = c.getBoundingClientRect();
          const dx = b.left - a.left;
          const dy = b.top - a.top;
          const resized = Math.abs(b.width - a.width) > 1 || Math.abs(b.height - a.height) > 1;
          const far = Math.abs(dx) > 160 || Math.abs(dy) > 160;
          if (resized || far) rise(c);
          else if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
            c.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: 280, easing: EASE });
          }
        });
        say(shown.length);
        // URL keeps the view (no new history entry)
        try {
          const u = new URL(window.location.href);
          Object.entries(PARAM).forEach(([key, p]) => { if (state[key] === 'all') u.searchParams.delete(p); else u.searchParams.set(p, state[key]); });
          window.history.replaceState(window.history.state, '', u);
        } catch (e) { /* file:// or sandboxed frames */ }
      }
    };

    sec.addEventListener('click', (e) => {
      if (e.target.closest('[data-tic-reset]')) {
        apply({ format: 'all', line: 'all' }, true);
        const firstOn = sec.querySelector('.tic-seg__track > button[aria-checked="true"]');
        if (firstOn) firstOn.focus();
      }
    });

    // initial view: from the URL (a shared link, Back from a product page, or a format link with ?line=)
    let u = null;
    try { u = new URL(window.location.href); } catch (e) { /* ignore */ }
    const init = {};
    if (u) Object.entries(PARAM).forEach(([key, p]) => { const v = u.searchParams.get(p); if (valid(key, v)) init[key] = v; });
    if (Object.keys(init).length && !design) apply(init, false);
    else layout();
    tracks.forEach((t) => { placeInk(t, false); revealOpt(t, false); });
    watchTracks(tracks);
  };

  /* ---------- header "Read more" (phones) ---------- */
  const setupMore = (rest) => {
    if (rest.dataset.ticReady) return;
    rest.dataset.ticReady = '1';
    const btn = rest.parentElement && rest.parentElement.querySelector('.tic-ch__more');
    if (!btn) return;
    const check = () => {
      if (rest.classList.contains('is-open')) return;
      const clamp = phone.matches && rest.textContent.trim().length > 0;
      rest.classList.toggle('is-clamped', clamp);
      btn.hidden = !clamp;
      btn.setAttribute('aria-expanded', clamp ? 'false' : 'true');
      btn.textContent = btn.dataset.more || 'Read more';
    };
    btn.addEventListener('click', () => {
      const open = !rest.classList.contains('is-open');
      rest.classList.toggle('is-open', open);
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      btn.textContent = open ? btn.dataset.less || 'Show less' : btn.dataset.more || 'Read more';
      if (open) {
        rest.classList.remove('is-clamped');
        if (!reduce.matches) rest.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 200, easing: 'ease' });
      } else {
        check();
      }
    });
    check();
    phone.addEventListener('change', () => { rest.classList.remove('is-open'); check(); });
  };

  const init = (scope) => {
    scope.querySelectorAll('[data-tic-cg]').forEach(setupGrid);
    scope.querySelectorAll('[data-tic-more]').forEach(setupMore);
  };
  doc.addEventListener('shopify:section:load', (e) => init(e.target));
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', () => init(doc));
  else init(doc);
})();
